import React from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const OperationalSection = ({ formData, updateFormData, errors }) => {
  const handleOccupancyChange = (value) => {
    updateFormData('operational', { ...formData?.operational, occupancyRate: parseInt(value) });
  };

  const handleDaysOpenChange = (value) => {
    updateFormData('operational', { ...formData?.operational, daysOpenPerMonth: parseInt(value) || 0 });
  };

  const handleCostModelChange = (model) => {
    updateFormData('operational', { ...formData?.operational, costModel: model });
  };

  const getOccupancyTag = () => {
    const rate = formData?.operational?.occupancyRate;
    if (rate >= 80) return { label: 'Excellent', color: 'bg-success text-success-foreground' };
    if (rate >= 60) return { label: 'Good', color: 'bg-primary text-primary-foreground' };
    if (rate >= 40) return { label: 'Average', color: 'bg-warning text-warning-foreground' };
    return { label: 'Low', color: 'bg-destructive text-destructive-foreground' };
  };

  const costModels = [
    {
      id: 'lean',
      name: 'Lean Operations',
      range: '30-45%',
      description: 'Minimal staff, basic maintenance, owner-operated',
      icon: 'Minimize2'
    },
    {
      id: 'standard',
      name: 'Standard Operations',
      range: '45-60%',
      description: 'Professional management, regular maintenance, amenities',
      icon: 'BarChart3'
    },
    {
      id: 'premium',
      name: 'Premium Operations',
      range: '60-75%',
      description: 'Full-service resort, extensive amenities, premium staff',
      icon: 'Crown'
    }
  ];

  const occupancyTag = getOccupancyTag();

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
          <Icon name="Settings" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-heading font-bold text-foreground">Operational Parameters</h3>
          <p className="text-sm text-muted-foreground">Define your operational model and expectations</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Occupancy Rate Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Expected Occupancy Rate</label>
            <div className="flex items-center space-x-2">
              <span className="text-lg font-bold text-primary">{formData?.operational?.occupancyRate}%</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${occupancyTag?.color}`}>
                {occupancyTag?.label}
              </span>
            </div>
          </div>
          <div className="relative">
            <input
              type="range"
              min={10}
              max={90}
              value={formData?.operational?.occupancyRate}
              onChange={(e) => handleOccupancyChange(e?.target?.value)}
              className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>10%</span>
              <span>90%</span>
            </div>
          </div>
        </div>

        {/* Days Open Per Month */}
        <Input
          label="Days Open Per Month"
          type="number"
          placeholder="30"
          description="How many days per month will you operate?"
          value={formData?.operational?.daysOpenPerMonth}
          onChange={(e) => handleDaysOpenChange(e?.target?.value)}
          error={errors?.daysOpenPerMonth}
          min="1"
          max="31"
          required
        />

        {/* Cost Model Selection */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">Operational Cost Model</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {costModels?.map((model) => (
              <Button
                key={model?.id}
                variant={formData?.operational?.costModel === model?.id ? 'default' : 'outline'}
                onClick={() => handleCostModelChange(model?.id)}
                className={`h-auto p-6 flex flex-col items-start text-left space-y-4 transition-all duration-200 ${
                  formData?.operational?.costModel === model?.id 
                    ? 'border-2 border-primary shadow-lg shadow-primary/20 ring-2 ring-primary/10' 
                    : 'border-2 border-border hover:border-primary/50 shadow-md hover:shadow-lg hover:shadow-primary/10 hover:ring-1 hover:ring-primary/5'
                }`}
              >
                <div className="flex items-center space-x-2 w-full">
                  <Icon name={model?.icon} size={20} />
                  <span className="font-semibold text-sm">{model?.name}</span>
                </div>
                <div className="text-sm font-medium opacity-90">{model?.range} of revenue</div>
                <div className="text-sm opacity-80 leading-relaxed">{model?.description}</div>
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Info" size={16} />
          <span>
            Industry average occupancy: 55-65% | Seasonal parks: 40-50% | Year-round parks: 60-75%
          </span>
        </div>
      </div>
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: var(--color-primary);
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default OperationalSection;